import { useTranslation } from 'react-i18next';
import type { PublicQuestion } from 'shared';
import { isAnswered, type AnswerDraft } from '@/lib/exam-session';
import { cn } from '@/lib/utils';

export type QuestionNavigatorProps = {
  questions: PublicQuestion[];
  answers: Record<string, AnswerDraft>;
  currentIndex: number;
  /** จำนวนข้อต่อหน้า — ใช้ไฮไลต์ทุกข้อที่อยู่ในหน้าเดียวกัน */
  questionsPerPage?: number;
  onJump: (index: number) => void;
};

/** ผังข้อสอบ 3 สถานะ — ใช้ทั้งสีและสัญลักษณ์ (ธง / ขีดเส้นใต้) ไม่พึ่งสีอย่างเดียว */
export function QuestionNavigator({
  questions,
  answers,
  currentIndex,
  questionsPerPage = 1,
  onJump,
}: QuestionNavigatorProps) {
  const { t } = useTranslation();

  return (
    <nav aria-label={t('exam.navigatorTitle')} className="mt-4">
      <ul className="grid grid-cols-8 gap-1.5 sm:grid-cols-10">
        {questions.map((question, index) => {
          const draft = answers[question.id];
          const answered = isAnswered(draft);
          const flagged = draft?.isFlagged ?? false;
          const isCurrent = index >= currentIndex && index < currentIndex + questionsPerPage;

          return (
            <li key={question.id}>
              <button
                type="button"
                onClick={() => onJump(index)}
                aria-current={isCurrent ? 'true' : undefined}
                aria-label={`${t('exam.goToQuestion', { number: index + 1 })}${
                  answered ? ` — ${t('exam.navigatorLegend.answered')}` : ` — ${t('exam.navigatorLegend.unanswered')}`
                }${flagged ? ` — ${t('exam.navigatorLegend.flagged')}` : ''}`}
                className={cn(
                  'relative flex h-10 w-full items-center justify-center rounded-md border text-sm font-medium transition-colors',
                  answered
                    ? 'border-emerald-600 bg-emerald-50 text-emerald-900'
                    : 'border-slate-300 bg-white text-slate-600',
                  flagged && 'border-amber-500 bg-amber-50 text-amber-900',
                  isCurrent && 'ring-2 ring-slate-900 ring-offset-1',
                )}
              >
                {index + 1}
                {flagged && (
                  <span aria-hidden className="absolute -top-1 -right-1 text-xs">
                    ⚑
                  </span>
                )}
                {answered && !flagged && (
                  <span aria-hidden className="absolute bottom-0.5 h-0.5 w-4 rounded bg-emerald-600" />
                )}
              </button>
            </li>
          );
        })}
      </ul>

      <ul className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-600">
        <li className="flex items-center gap-1.5">
          <span aria-hidden className="h-3 w-3 rounded border border-emerald-600 bg-emerald-50" />
          {t('exam.navigatorLegend.answered')}
        </li>
        <li className="flex items-center gap-1.5">
          <span aria-hidden className="h-3 w-3 rounded border border-slate-300 bg-white" />
          {t('exam.navigatorLegend.unanswered')}
        </li>
        <li className="flex items-center gap-1.5">
          <span aria-hidden>⚑</span>
          {t('exam.navigatorLegend.flagged')}
        </li>
      </ul>
    </nav>
  );
}
